// MonthViewMobile.jsx
import "./MonthViewMobile.scss";

import { useEffect, useMemo, useRef, useState } from "react";

function MonthViewMobile({ currentDate: initialDate, events = [], onDayClick, closeDiary = [] }) {
  const containerRef = useRef(null);

  const [currentMonth, setCurrentMonth] = useState(
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
  );
  const [selectedDay, setSelectedDay] = useState(initialDate);

  // ✅ esconder/mostrar calendário ao rolar
  const [calendarHidden, setCalendarHidden] = useState(false);
  const lastScrollRef = useRef(0);
  const rafRef = useRef(null);

  // mantém sincronizado se o pai mudar a data
  useEffect(() => {
    const next = new Date(initialDate.getFullYear(), initialDate.getMonth(), 1);
    setCurrentMonth(next);
    setSelectedDay(initialDate);
  }, [initialDate]);

  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDay(today);
    onDayClick?.(today);
  };

  const isCurrentMonth = () => {
    const today = new Date();
    return (
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const firstDayOfWeek = firstDay.getDay();

    const lastDay = new Date(year, month + 1, 0);
    const lastDate = lastDay.getDate();

    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days = [];

    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }

    for (let i = 1; i <= lastDate; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const calendarDays = getCalendarDays();

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => date.toDateString() === selectedDay.toDateString();

  // =========================
  // closeDiary info (UI)
  // =========================
  const typeLabel = (type) => {
    const t = String(type || "").toUpperCase();
    if (t === "VACATION" || t === "FERIAS" || t === "FÉRIAS") return "Férias";
    if (t === "HOLIDAY" || t === "FERIADO") return "Feriado";
    if (t === "DAY_OFF" || t === "FOLGA") return "Folga";
    if (t === "BLOCK") return "Bloqueio";
    return "Bloqueio";
  };

  const isoToDate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
    if ([y, m, d].some((n) => Number.isNaN(n))) return null;
    const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };

  const normalizeRange = (item) => {
    const s = isoToDate(item?.startDate);
    const e = isoToDate(item?.endDate ?? item?.startDate);
    if (!s || !e) return null;

    const start = s.getTime() <= e.getTime() ? s : e;
    const end = s.getTime() <= e.getTime() ? e : s;

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const hasTimeRange = !!(item?.startTime && item?.endTime);

    return {
      ...item,
      __start: start,
      __end: end,
      __label: typeLabel(item?.type),
      __hasTimeRange: hasTimeRange,
      __timeLabel: hasTimeRange ? `${item.startTime}–${item.endTime}` : "dia todo",
    };
  };

  const normalizedCloseDiary = useMemo(() => {
    return (closeDiary || []).map(normalizeRange).filter(Boolean);
  }, [closeDiary]);

  const dayHasBlock = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return normalizedCloseDiary.some((b) => d >= b.__start && d <= b.__end);
  };

  const getCloseDiaryForDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return normalizedCloseDiary.filter((b) => d >= b.__start && d <= b.__end);
  };

  const formatBRShort = (date) =>
    new Date(date).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

  const buildBlockTooltip = (date) => {
    const blocks = getCloseDiaryForDate(date);
    if (!blocks.length) return "";
    const parts = blocks.map((b) => {
      const start = formatBRShort(b.__start);
      const end = formatBRShort(b.__end);
      const rangeText = start === end ? start : `${start}–${end}`;
      const timeText = b.__hasTimeRange ? ` · ${b.__timeLabel}` : " · dia todo";
      return `${b.__label} (${rangeText}${timeText})`;
    });
    return `Agenda bloqueada: ${parts.join(" • ")}`;
  };

  // =========================
  // ✅ Eventos do calendário
  // =========================

  // Indexa contagem por dia
  const eventsByDayKey = useMemo(() => {
    const map = new Map();
    for (const ev of events || []) {
      const d = new Date(ev.start);
      const key = d.toDateString();
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [events]);

  // ✅ Indexa se há promoção por dia (se existir event.promotionName)
  const promoByDayKey = useMemo(() => {
    const map = new Map();
    for (const ev of events || []) {
      const d = new Date(ev.start);
      const key = d.toDateString();
      if (ev?.promotionName) map.set(key, true);
    }
    return map;
  }, [events]);

  // ✅ Indexa se há ASSINANTE (hasClub) por dia
  const clubByDayKey = useMemo(() => {
    const map = new Map();
    for (const ev of events || []) {
      const d = new Date(ev.start);
      const key = d.toDateString();
      if (ev?.hasClub) map.set(key, true);
    }
    return map;
  }, [events]);

  const getDayEventsCount = (date) => eventsByDayKey.get(date.toDateString()) || 0;
  const dayHasPromo = (date) => promoByDayKey.get(date.toDateString()) === true;
  const dayHasClub = (date) => clubByDayKey.get(date.toDateString()) === true;

  const getDayEvents = (date) => {
    return (events || []).filter((event) => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const handleDayClick = (day) => {
    setSelectedDay(day.date);
    onDayClick?.(day.date);
  };

  // ✅ eventos do dia selecionado ORDENADOS por horário
  const selectedDayEvents = useMemo(() => {
    const list = getDayEvents(selectedDay);

    return [...list].sort((a, b) => {
      const sa = new Date(a.start).getTime();
      const sb = new Date(b.start).getTime();
      if (sa !== sb) return sa - sb;

      const ea = new Date(a.end).getTime();
      const eb = new Date(b.end).getTime();
      return ea - eb;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, selectedDay]);

  const selectedDayBlocks = useMemo(
    () => getCloseDiaryForDate(selectedDay),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [normalizedCloseDiary, selectedDay]
  );

  // ✅ scroll: pra baixo esconde calendário, pra cima mostra
  useEffect(() => {
    const el = containerRef.current;

    const hasOwnScroll =
      el && el.scrollHeight > el.clientHeight + 5 && getComputedStyle(el).overflowY !== "visible";

    const getScrollTop = () => (hasOwnScroll ? el.scrollTop : window.scrollY || 0);

    const onScroll = () => {
      if (rafRef.current) return;

      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;

        const currentTop = getScrollTop();
        const diff = currentTop - lastScrollRef.current;

        if (Math.abs(diff) < 8) return;

        if (currentTop <= 8) {
          setCalendarHidden(false);
          lastScrollRef.current = currentTop;
          return;
        }

        if (diff > 0) setCalendarHidden(true);
        else setCalendarHidden(false);

        lastScrollRef.current = currentTop;
      });
    };

    lastScrollRef.current = getScrollTop();

    const target = hasOwnScroll ? el : window;
    target.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      target.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, []);

  const formatTime = (iso) =>
    new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  return (
    <div
      ref={containerRef}
      className={`month-view-mobile ${calendarHidden ? "month-view-mobile--calendar-hidden" : ""}`}
    >
      {/* Header */}
      <div className="month-view-mobile__header">
        <button className="month-view-mobile__nav-btn" onClick={goToPreviousMonth}>
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>

        <div className="month-view-mobile__month-info">
          <span className="month-view-mobile__month-name">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>

          {!isCurrentMonth() && (
            <button className="month-view-mobile__today-btn" onClick={goToToday}>
              Hoje
            </button>
          )}
        </div>

        <button className="month-view-mobile__nav-btn" onClick={goToNextMonth}>
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
      </div>

      {/* Calendário */}
      <div className="month-view-mobile__calendar">
        <div className="month-view-mobile__weekdays">
          {dayNames.map((day, index) => (
            <div key={index} className="month-view-mobile__weekday">
              {day}
            </div>
          ))}
        </div>

        <div className="month-view-mobile__days">
          {calendarDays.map((day, index) => {
            const eventsCount = getDayEventsCount(day.date);
            const dots = Math.min(eventsCount, 3);
            const more = eventsCount - dots;

            const hasBlock = dayHasBlock(day.date);
            const tooltip = hasBlock ? buildBlockTooltip(day.date) : undefined;

            const hasPromo = dayHasPromo(day.date);
            const hasClub = dayHasClub(day.date);

            return (
              <div
                key={index}
                className={`month-view-mobile__day
                  ${!day.isCurrentMonth ? "month-view-mobile__day--other-month" : ""}
                  ${isToday(day.date) ? "month-view-mobile__day--today" : ""}
                  ${isSelected(day.date) ? "month-view-mobile__day--selected" : ""}
                  ${hasBlock ? "month-view-mobile__day--blocked" : ""}
                  ${hasPromo ? "month-view-mobile__day--has-promo" : ""}
                  ${hasClub ? "month-view-mobile__day--has-club" : ""}
                `}
                onClick={() => handleDayClick(day)}
                title={tooltip}
                aria-label={tooltip || `${eventsCount} eventos`}
              >
                <span className="month-view-mobile__day-number">{day.date.getDate()}</span>

                {/* ✅ indicador de bloqueio */}
                {hasBlock && (
                  <div className="month-view-mobile__day-block-indicator" aria-hidden="true">
                    <span className="month-view-mobile__day-block-dot" />
                    <span className="month-view-mobile__day-block-text">Bloqueado</span>
                  </div>
                )}

                {/* ✅ indicador de clube no dia */}
                {hasClub && (
                  <div className="month-view-mobile__day-club-indicator" aria-hidden="true">
                    <span className="month-view-mobile__day-club-dot" />
                    <span className="month-view-mobile__day-club-text">Clube</span>
                  </div>
                )}

                {/* ✅ indicador de promoção no dia */}
                {hasPromo && (
                  <div className="month-view-mobile__day-promo-indicator" aria-hidden="true">
                    <span className="month-view-mobile__day-promo-dot" />
                    <span className="month-view-mobile__day-promo-text">Promo</span>
                  </div>
                )}

                <div className="month-view-mobile__day-indicators" aria-label={`${eventsCount} eventos`}>
                  {eventsCount > 0 && (
                    <>
                      <div className="month-view-mobile__day-dots">
                        {Array.from({ length: dots }).map((_, i) => (
                          <span key={i} className="month-view-mobile__day-dot" />
                        ))}
                      </div>

                      {more > 0 && (
                        <span className="month-view-mobile__day-more" title={`${eventsCount} eventos`}>
                          +{more}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lista do dia selecionado */}
      <div className="month-view-mobile__events">
        <div className="month-view-mobile__events-header">
          <h3 className="month-view-mobile__events-title">
            {selectedDay.toLocaleDateString("pt-BR", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </h3>

          <span className="month-view-mobile__events-count">
            {selectedDayEvents.length} {selectedDayEvents.length === 1 ? "evento" : "eventos"}
          </span>
        </div>

        {selectedDayBlocks.length > 0 && (
          <div className="month-view-mobile__block-banner" role="note">
            <div className="month-view-mobile__block-banner-icon" aria-hidden="true">
              ⛔
            </div>

            <div className="month-view-mobile__block-banner-content">
              <div className="month-view-mobile__block-banner-title">Agenda bloqueada</div>
              <div className="month-view-mobile__block-banner-sub">
                {selectedDayBlocks.map((b, i) => {
                  const start = formatBRShort(b.__start);
                  const end = formatBRShort(b.__end);
                  const rangeText = start === end ? start : `${start}–${end}`;

                  const timeText = b.__hasTimeRange ? b.__timeLabel : b.reason || "dia todo";

                  return (
                    <span
                      key={`${b.employeeId ?? "emp"}-${b.startDate ?? "s"}-${b.endDate ?? "e"}-${
                        b.startTime ?? "st"
                      }-${b.endTime ?? "et"}-${i}`}
                      className="month-view-mobile__block-pill"
                    >
                      {b.__label} · {rangeText} · {timeText}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {selectedDayEvents.length === 0 ? (
          <div className="month-view-mobile__events-empty">
            <div className="month-view-mobile__events-empty-title">Nenhum agendamento</div>
            <div className="month-view-mobile__events-empty-sub">
              Toque em um dia no calendário para ver os agendamentos.
            </div>
          </div>
        ) : (
          <div className="month-view-mobile__timeline">
            <div className="month-view-mobile__timeline-line" aria-hidden="true" />

            <div className="month-view-mobile__timeline-list">
              {selectedDayEvents.map((event, idx) => {
                const start = formatTime(event.start);
                const end = formatTime(event.end);

                const isPromotion = Boolean(event?.promotionName);
                const hasClub = Boolean(event?.hasClub);

                return (
                  <div
                    key={event.id ?? idx}
                    className={`month-view-mobile__timeline-item ${
                      isPromotion ? "month-view-mobile__timeline-item--promo" : ""
                    } ${hasClub ? "month-view-mobile__timeline-item--club" : ""}`}
                  >
                    <div className="month-view-mobile__timeline-time">
                      <div className="month-view-mobile__timeline-time-start">{start}</div>
                      <div className="month-view-mobile__timeline-time-end">{end}</div>
                    </div>

                    <div
                      className={`month-view-mobile__timeline-dot ${
                        isPromotion ? "month-view-mobile__timeline-dot--promo" : ""
                      } ${hasClub ? "month-view-mobile__timeline-dot--club" : ""}`}
                      aria-hidden="true"
                    />

                    <div
                      className={`month-view-mobile__event month-view-mobile__event--timeline ${
                        isPromotion ? "month-view-mobile__event--promotion" : ""
                      } ${hasClub ? "month-view-mobile__event--club" : ""}`}
                      title={hasClub ? "Cliente assinante do clube" : undefined}
                    >
                      {/* ✅ Badge Clube no card */}
                      {hasClub && (
                        <div className="month-view-mobile__event-club-badge" title="Cliente assinante">
                          ⭐ Clube
                        </div>
                      )}

                      {/* ✅ Badge Promo no card */}
                      {isPromotion && (
                        <div
                          className="month-view-mobile__event-promo-badge"
                          title={`Promoção: ${event.promotionName}`}
                        >
                          🔥 Promo • {event.promotionName}
                        </div>
                      )}

                      <div className="month-view-mobile__event-title">
                        {event.title}
                        {hasClub && <span className="month-view-mobile__event-club-inline"> • Clube</span>}
                      </div>

                      {event.client && (
                        <div className="month-view-mobile__event-client">{event.client}</div>
                      )}

                      <div className="month-view-mobile__event-time">
                        {start} - {end}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ height: "calc(50px + 72px)" }} />
          </div>
        )}
      </div>
    </div>
  );
}

export default MonthViewMobile;
